import { apiFetch, apiFetchNullable, ApiError, useMockApi } from '@/lib/api/client'
import {
  mapSendMessageResult,
  mapSessionDetails,
  mapSessionListItem,
  toSessionSettingsPayload,
} from '@/lib/api/mappers'
import { buildSettingsBadge, mergeSettings } from '@/lib/chat/settings-badge'
import { readSseStream } from '@/lib/sse'
import type {
  ChatMessage,
  ChatSession,
  SendMessageResult,
  SessionSettings,
  StreamMessageHandlers,
} from '@/types/chat'
import { DEFAULT_SESSION_SETTINGS } from '@/types/chat'

export interface SessionListQuery {
  status?: string
  page?: number
  perPage?: number
}

export interface CreateSessionInput {
  modelId: string
  categoryId: string
  title?: string
  systemPrompt?: string
}

export interface SessionApi {
  listSessions(query?: SessionListQuery): Promise<{ items: ChatSession[]; total: number }>
  getSession(sessionId: string): Promise<ChatSession | null>
  createSession(input: CreateSessionInput): Promise<ChatSession>
  closeSession(sessionId: string): Promise<ChatSession>
  sendMessage(
    sessionId: string,
    promptText: string,
    settingsOverride?: SessionSettings,
  ): Promise<SendMessageResult>
  sendMessageStream(
    sessionId: string,
    promptText: string,
    settingsOverride: SessionSettings | undefined,
    handlers: StreamMessageHandlers,
    signal?: AbortSignal,
  ): Promise<void>
  updateSession(
    sessionId: string,
    patch: {
      systemPrompt?: string
      defaultSettings?: SessionSettings
      title?: string
    },
  ): Promise<ChatSession>
  archiveSession(sessionId: string): Promise<ChatSession>
  restoreSession(sessionId: string): Promise<ChatSession>
  reopenSession(sessionId: string): Promise<ChatSession>
  duplicateSession(sessionId: string): Promise<ChatSession>
}

const nowIso = () => new Date().toISOString()
const MOCK_SESSIONS_STORAGE_KEY = 'modelscout_mock_sessions'

function loadStoredSessions(): ChatSession[] | null {
  if (typeof sessionStorage === 'undefined') return null

  const raw = sessionStorage.getItem(MOCK_SESSIONS_STORAGE_KEY)
  if (!raw) return null

  try {
    return JSON.parse(raw) as ChatSession[]
  } catch {
    return null
  }
}

function storeSessions(sessions: ChatSession[]) {
  if (typeof sessionStorage === 'undefined') return
  sessionStorage.setItem(MOCK_SESSIONS_STORAGE_KEY, JSON.stringify(sessions))
}

function createSeedSessions(): ChatSession[] {
  const settings: SessionSettings = {
    temperature: 0.7,
    topP: 0.9,
    maxTokens: 4096,
    jsonMode: false,
  }

  return [
    {
      id: 'session-gpt4o',
      modelId: 'model-1',
      modelName: 'GPT-4o',
      categoryName: 'Coding',
      title: 'GPT-4o / Coding',
      status: 'open',
      systemPrompt: 'You are a senior Go developer.',
      defaultSettings: settings,
      createdAt: nowIso(),
      lastMessageAt: nowIso(),
      messages: [
        {
          id: 'msg-1',
          promptRequestId: 'req-1',
          promptText: 'How do I implement a context-aware logger in Go?',
          settingsSnapshot: settings,
          settingsBadge: buildSettingsBadge(settings),
          response: {
            id: 'resp-1',
            normalizedText: 'To implement a context-aware logger, attach context values to each log call.',
            tokensInput: 45,
            tokensOutput: 312,
            estimatedCost: 0.000034,
            latencyMs: 1240,
            status: 'completed',
          },
          createdAt: nowIso(),
        },
      ],
    },
    {
      id: 'session-claude',
      modelId: 'model-2',
      modelName: 'Claude 3.5',
      categoryName: 'Coding',
      title: 'Claude 3.5 / Coding',
      status: 'open',
      systemPrompt: 'You are a helpful coding assistant.',
      defaultSettings: { ...settings, temperature: 0.5 },
      createdAt: nowIso(),
      messages: [],
    },
    {
      id: 'session-llama',
      modelId: 'model-3',
      modelName: 'Llama 3.3',
      categoryName: 'Research',
      title: 'Llama 3.3 / Research',
      status: 'open',
      systemPrompt: 'You summarize research papers clearly.',
      defaultSettings: { ...settings, temperature: 0.3 },
      createdAt: nowIso(),
      messages: [],
    },
  ]
}

class MockSessionApi implements SessionApi {
  private sessions: ChatSession[] = loadStoredSessions() ?? createSeedSessions()

  private persist() {
    storeSessions(this.sessions)
  }

  async listSessions(query: SessionListQuery = {}): Promise<{ items: ChatSession[]; total: number }> {
    let items = [...this.sessions]

    if (query.status && query.status !== 'all') {
      items = items.filter((session) => session.status === query.status)
    }

    items.sort((left, right) =>
      (right.lastMessageAt ?? right.createdAt).localeCompare(left.lastMessageAt ?? left.createdAt),
    )

    return { items, total: items.length }
  }

  async getSession(sessionId: string): Promise<ChatSession | null> {
    const session = this.sessions.find((item) => item.id === sessionId)
    return session ? structuredClone(session) : null
  }

  async createSession(input: CreateSessionInput): Promise<ChatSession> {
    const settings: SessionSettings = { ...DEFAULT_SESSION_SETTINGS }
    const session: ChatSession = {
      id: `session-${crypto.randomUUID()}`,
      modelId: input.modelId,
      modelName: input.title ?? 'New chat',
      title: input.title ?? 'New chat',
      status: 'open',
      systemPrompt: input.systemPrompt,
      defaultSettings: settings,
      messages: [],
      createdAt: nowIso(),
    }

    this.sessions.push(session)
    this.persist()
    return structuredClone(session)
  }

  async closeSession(sessionId: string): Promise<ChatSession> {
    const session = this.sessions.find((item) => item.id === sessionId)
    if (!session) throw new Error('session_not_found')

    session.status = 'closed'
    session.closedAt = nowIso()
    this.persist()
    return structuredClone(session)
  }

  async sendMessage(
    sessionId: string,
    promptText: string,
    settingsOverride?: SessionSettings,
  ): Promise<SendMessageResult> {
    const session = this.sessions.find((item) => item.id === sessionId)
    if (!session) throw new Error('session_not_found')

    const snapshot = mergeSettings(session.defaultSettings, settingsOverride)
    const message: ChatMessage = {
      id: `msg-${crypto.randomUUID()}`,
      promptRequestId: `req-${crypto.randomUUID()}`,
      promptText,
      settingsSnapshot: snapshot,
      settingsBadge: buildSettingsBadge(snapshot),
      response: {
        id: `resp-${crypto.randomUUID()}`,
        normalizedText: `Mock response for: ${promptText.slice(0, 80)}`,
        tokensInput: 20,
        tokensOutput: 80,
        estimatedCost: 0.00001,
        latencyMs: 420,
        status: 'completed',
      },
      createdAt: nowIso(),
    }

    session.messages.push(message)
    session.lastMessageAt = nowIso()
    this.persist()

    return {
      requestId: message.promptRequestId,
      responseId: message.response!.id,
      normalizedText: message.response!.normalizedText,
      tokensInput: message.response!.tokensInput,
      tokensOutput: message.response!.tokensOutput,
      estimatedCost: message.response!.estimatedCost,
      latencyMs: message.response!.latencyMs,
      status: message.response!.status,
    }
  }

  async sendMessageStream(
    sessionId: string,
    promptText: string,
    settingsOverride: SessionSettings | undefined,
    handlers: StreamMessageHandlers,
    signal?: AbortSignal,
  ): Promise<void> {
    const session = this.sessions.find((item) => item.id === sessionId)
    if (!session) throw new Error('session_not_found')

    const snapshot = mergeSettings(session.defaultSettings, settingsOverride)
    const requestId = `req-${crypto.randomUUID()}`
    const responseId = `resp-${crypto.randomUUID()}`
    handlers.onMeta?.({ requestId, responseId })

    const parts = ['Mock ', 'response ', 'for: ', promptText.slice(0, 80)]
    let text = ''
    for (const part of parts) {
      if (signal?.aborted) {
        handlers.onCancelled?.(responseId)
        return
      }
      await new Promise((resolve) => setTimeout(resolve, 5))
      text += part
      handlers.onChunk(part)
    }

    const message: ChatMessage = {
      id: requestId,
      promptRequestId: requestId,
      promptText,
      settingsSnapshot: snapshot,
      settingsBadge: buildSettingsBadge(snapshot),
      response: {
        id: responseId,
        normalizedText: text,
        tokensInput: 20,
        tokensOutput: 80,
        estimatedCost: 0.00001,
        latencyMs: 420,
        status: 'completed',
      },
      createdAt: nowIso(),
    }

    session.messages.push(message)
    session.lastMessageAt = nowIso()
    this.persist()

    handlers.onDone({
      requestId,
      responseId,
      normalizedText: text,
      tokensInput: 20,
      tokensOutput: 80,
      estimatedCost: 0.00001,
      latencyMs: 420,
      status: 'completed',
    })
  }

  async updateSession(
    sessionId: string,
    patch: {
      systemPrompt?: string
      defaultSettings?: SessionSettings
      title?: string
    },
  ): Promise<ChatSession> {
    const session = this.sessions.find((item) => item.id === sessionId)
    if (!session) throw new Error('session_not_found')

    if (patch.systemPrompt !== undefined) session.systemPrompt = patch.systemPrompt
    if (patch.defaultSettings !== undefined) session.defaultSettings = patch.defaultSettings
    if (patch.title !== undefined) session.title = patch.title

    this.persist()
    return structuredClone(session)
  }

  async archiveSession(sessionId: string): Promise<ChatSession> {
    const session = this.sessions.find((item) => item.id === sessionId)
    if (!session) throw new Error('session_not_found')
    session.status = 'archived'
    session.closedAt = session.closedAt ?? nowIso()
    this.persist()
    return structuredClone(session)
  }

  async restoreSession(sessionId: string): Promise<ChatSession> {
    const session = this.sessions.find((item) => item.id === sessionId)
    if (!session) throw new Error('session_not_found')
    session.status = 'closed'
    this.persist()
    return structuredClone(session)
  }

  async reopenSession(sessionId: string): Promise<ChatSession> {
    const session = this.sessions.find((item) => item.id === sessionId)
    if (!session) throw new Error('session_not_found')
    session.status = 'open'
    session.closedAt = undefined
    this.persist()
    return structuredClone(session)
  }

  async duplicateSession(sessionId: string): Promise<ChatSession> {
    const source = this.sessions.find((item) => item.id === sessionId)
    if (!source) throw new Error('session_not_found')

    const copy: ChatSession = {
      ...structuredClone(source),
      id: `session-${crypto.randomUUID()}`,
      title: source.title.endsWith(' (copy)') ? source.title : `${source.title} (copy)`,
      status: 'open',
      closedAt: undefined,
      createdAt: nowIso(),
      lastMessageAt: undefined,
      messages: [],
    }

    this.sessions.push(copy)
    this.persist()
    return structuredClone(copy)
  }
}

class HttpSessionApi implements SessionApi {
  async listSessions(query: SessionListQuery = {}): Promise<{ items: ChatSession[]; total: number }> {
    const params = new URLSearchParams()
    if (query.status) params.set('status', query.status)
    if (query.page) params.set('page', String(query.page))
    if (query.perPage) params.set('per_page', String(query.perPage))

    const body = await apiFetch<{ items: unknown[]; total: number }>(`/api/v1/sessions?${params}`)
    return {
      items: body.items.map(mapSessionListItem),
      total: body.total,
    }
  }

  async getSession(sessionId: string): Promise<ChatSession | null> {
    const body = await apiFetchNullable<unknown>(`/api/v1/sessions/${sessionId}`)
    return body ? mapSessionDetails(body) : null
  }

  async createSession(input: CreateSessionInput): Promise<ChatSession> {
    const body = await apiFetch<unknown>('/api/v1/sessions', {
      method: 'POST',
      body: JSON.stringify({
        model_id: input.modelId,
        category_id: input.categoryId,
        title: input.title,
        system_prompt: input.systemPrompt,
      }),
    })

    const created = mapSessionListItem(body)
    const details = await this.getSession(created.id)
    return details ?? created
  }

  async closeSession(sessionId: string): Promise<ChatSession> {
    await apiFetch(`/api/v1/sessions/${sessionId}/close`, { method: 'POST' })
    const refreshed = await this.getSession(sessionId)
    if (!refreshed) throw new Error('session_close_failed')
    return refreshed
  }

  async sendMessage(
    sessionId: string,
    promptText: string,
    settingsOverride?: SessionSettings,
  ): Promise<SendMessageResult> {
    const body = await apiFetch<unknown>(`/api/v1/sessions/${sessionId}/messages`, {
      method: 'POST',
      body: JSON.stringify({
        prompt_text: promptText,
        settings_override: settingsOverride ? toSessionSettingsPayload(settingsOverride) : undefined,
        stream: false,
      }),
    })
    return mapSendMessageResult(body)
  }

  async sendMessageStream(
    sessionId: string,
    promptText: string,
    settingsOverride: SessionSettings | undefined,
    handlers: StreamMessageHandlers,
    signal?: AbortSignal,
  ): Promise<void> {
    const response = await fetch(`/api/v1/sessions/${sessionId}/messages`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
      },
      body: JSON.stringify({
        prompt_text: promptText,
        settings_override: settingsOverride ? toSessionSettingsPayload(settingsOverride) : undefined,
        stream: true,
      }),
      signal,
    })

    if (response.status === 401) {
      throw new ApiError('unauthorized', 401)
    }

    if (!response.ok) {
      let code: string | undefined
      try {
        const body = (await response.json()) as { error?: string }
        code = body.error
      } catch {
        // ignore
      }
      throw new ApiError(code ?? `http_${response.status}`, response.status, code)
    }

    await readSseStream(response, ({ event, data }) => {
      const payload = JSON.parse(data) as Record<string, unknown>

      if (event === 'meta') {
        handlers.onMeta?.({
          requestId: String(payload.request_id),
          responseId: String(payload.response_id),
        })
        return
      }

      if (event === 'chunk') {
        handlers.onChunk(String(payload.delta ?? ''))
        return
      }

      if (event === 'done') {
        handlers.onDone(mapSendMessageResult(payload))
        return
      }

      if (event === 'cancelled') {
        handlers.onCancelled?.(payload.response_id ? String(payload.response_id) : undefined)
        return
      }

      if (event === 'error') {
        handlers.onError(String(payload.error ?? 'stream_error'))
      }
    })
  }

  async updateSession(
    sessionId: string,
    patch: {
      systemPrompt?: string
      defaultSettings?: SessionSettings
      title?: string
    },
  ): Promise<ChatSession> {
    const body = await apiFetch<unknown>(`/api/v1/sessions/${sessionId}`, {
      method: 'PATCH',
      body: JSON.stringify({
        system_prompt: patch.systemPrompt,
        default_settings: patch.defaultSettings
          ? toSessionSettingsPayload(patch.defaultSettings)
          : undefined,
        title: patch.title,
      }),
    })
    return mapSessionDetails(body)
  }

  async archiveSession(sessionId: string): Promise<ChatSession> {
    await apiFetch(`/api/v1/sessions/${sessionId}/archive`, { method: 'POST' })
    const refreshed = await this.getSession(sessionId)
    if (!refreshed) throw new Error('session_archive_failed')
    return refreshed
  }

  async restoreSession(sessionId: string): Promise<ChatSession> {
    await apiFetch(`/api/v1/sessions/${sessionId}/restore`, { method: 'POST' })
    const refreshed = await this.getSession(sessionId)
    if (!refreshed) throw new Error('session_restore_failed')
    return refreshed
  }

  async reopenSession(sessionId: string): Promise<ChatSession> {
    await apiFetch(`/api/v1/sessions/${sessionId}/reopen`, { method: 'POST' })
    const refreshed = await this.getSession(sessionId)
    if (!refreshed) throw new Error('session_reopen_failed')
    return refreshed
  }

  async duplicateSession(sessionId: string): Promise<ChatSession> {
    const body = await apiFetch<unknown>(`/api/v1/sessions/${sessionId}/duplicate`, {
      method: 'POST',
    })
    const created = mapSessionListItem(body)
    const details = await this.getSession(created.id)
    if (!details) throw new Error('session_duplicate_failed')
    return details
  }
}

export const sessionApi: SessionApi = useMockApi() ? new MockSessionApi() : new HttpSessionApi()

export { DEFAULT_SESSION_SETTINGS }
